import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useSearch } from '@/hooks/useSearch';
import type { FetchSearchOptions, SearchHit } from '@/lib/search';

/**
 * Central command palette (replit.md §0.8, docs/UI_STANDARDS.md §3 — overlay).
 * One Cmd+K (⌘/Ctrl+K) search over everything: server-ranked, grouped by type,
 * with a contextual subtitle and per-hit actions. Every module registers into
 * the central index instead of building its own search — this is the only UI
 * surface that reads it. Keyboard-first: ↑/↓ move, Enter runs the primary action,
 * Esc closes. Dark-first, accessible (dialog + listbox/option roles).
 */

/** Estonian group headings (UI labels stay Estonian); unknown types fall back to the raw value. */
const TYPE_LABELS: Record<string, string> = {
  project: 'Projektid',
  document: 'Dokumendid',
  supplier: 'Tarnijad',
  client: 'Kliendid',
  sku: 'Tooted',
  amount: 'Summad',
};

function typeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Search transport options (base URL, access token, injectable fetch for tests). */
  searchOptions?: FetchSearchOptions;
  /** What "run" does; defaults to navigating to the hit's href. Injectable for tests. */
  onSelect?: (hit: SearchHit) => void;
}

function defaultSelect(hit: SearchHit): void {
  if (hit.href) {
    window.location.assign(hit.href);
  }
}

export function CommandPalette({ open, onOpenChange, searchOptions, onSelect }: CommandPaletteProps) {
  const [value, setValue] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { groups, isLoading, isError, query } = useSearch(open ? value : '', searchOptions);

  // Flatten the grouped hits into the keyboard-navigation order.
  const flat = useMemo(() => groups.flatMap((g) => g.results), [groups]);

  // Global ⌘/Ctrl+K to open — always mounted so the shortcut works when closed.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(true);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onOpenChange]);

  // Reset the box + focus the input each time the palette opens.
  useEffect(() => {
    if (open) {
      setValue('');
      setActiveIndex(0);
      inputRef.current?.focus();
    }
  }, [open]);

  // Keep the active row in range as results change.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Keep the highlighted option visible while arrowing through a long result list —
  // a primary-tool nicety so keyboard navigation never scrolls the active row out of
  // sight (feature-detected because jsdom has no scrollIntoView).
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, query]);

  if (!open) {
    return null;
  }

  function close() {
    onOpenChange(false);
  }

  function run(hit: SearchHit | undefined) {
    if (!hit) {
      return;
    }
    (onSelect ?? defaultSelect)(hit);
    close();
  }

  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (flat.length === 0 ? 0 : (i + 1) % flat.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (flat.length === 0 ? 0 : (i - 1 + flat.length) % flat.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(flat[activeIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  }

  const showHint = query.length === 0;
  const showEmpty = !showHint && !isLoading && !isError && flat.length === 0;

  let flatIndex = -1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 pt-[12vh]"
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Otsing"
        className="w-full max-w-xl overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onInputKeyDown}
          aria-label="Otsi"
          placeholder="Otsi projekte, dokumente, tarnijaid…"
          className="w-full border-b border-border bg-transparent px-4 py-3 text-sm outline-none placeholder:text-muted-foreground"
        />

        <div ref={listRef} role="listbox" aria-label="Otsingutulemused" className="max-h-80 overflow-y-auto py-1">
          {isError && (
            <p role="alert" className="px-4 py-3 text-sm text-destructive">
              Otsing ebaõnnestus
            </p>
          )}
          {isLoading && <p className="px-4 py-3 text-sm text-muted-foreground">Otsin…</p>}
          {showHint && (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              Alusta trükkimist, et otsida üle kõikide moodulite.
            </p>
          )}
          {showEmpty && <p className="px-4 py-3 text-sm text-muted-foreground">Tulemusi ei leitud</p>}

          {!isLoading &&
            !isError &&
            groups.map((group) => (
              <div key={group.type} className="py-1">
                <p className="px-4 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {typeLabel(group.type)}
                </p>
                {group.results.map((hit) => {
                  flatIndex += 1;
                  const index = flatIndex;
                  const active = index === activeIndex;
                  return (
                    <div
                      key={`${hit.entityType}:${hit.entityId}`}
                      role="option"
                      aria-selected={active}
                      data-active={active}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => run(hit)}
                      className={cn(
                        'flex cursor-pointer items-center justify-between gap-3 px-4 py-2 text-sm',
                        active ? 'bg-accent text-accent-foreground' : 'text-foreground',
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {hit.icon && <span aria-hidden="true">{hit.icon}</span>}
                        <span className="min-w-0">
                          <span className="block truncate">{hit.title}</span>
                          {hit.subtitle && (
                            <span className="block truncate text-xs text-muted-foreground">{hit.subtitle}</span>
                          )}
                        </span>
                      </span>
                      {hit.actions.length > 0 && (
                        <span className="flex shrink-0 gap-1">
                          {hit.actions.map((action) => (
                            <button
                              key={action.type}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                run({ ...hit, href: action.href });
                              }}
                              className="rounded border border-border px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                            >
                              {action.label}
                            </button>
                          ))}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
          <span>Otsing üle kõikide moodulite</span>
          <span className="flex items-center gap-3">
            <span>
              <kbd className="rounded border border-border px-1">↑</kbd>{' '}
              <kbd className="rounded border border-border px-1">↓</kbd> liigu
            </span>
            <span>
              <kbd className="rounded border border-border px-1">↵</kbd> ava
            </span>
            <span>
              <kbd className="rounded border border-border px-1">Esc</kbd> sulge
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
